/////////////////////////////////////////////////////////////////////////////////////////////////////////
// 화일명 : ModalContext.ts
// 설명   : 모달(팝업) Context 정의 - CommonModal.tsx에서 분리
//         - AgGrid.tsx가 useRefPopupModal()을 쓰기 위해 CommonModal.tsx를 import하면 순환참조가
//           발생함(CommonModal.tsx가 이미 AgGrid.tsx를 import하고 있음) - 그래서 Context/타입/훅만
//           의존성 없는 이 파일로 분리하고, AgGrid.tsx는 이 파일에서 직접 가져다 씀.
//         - CommonModal.tsx는 이 파일의 훅들을 그대로 재수출(re-export)해서, 기존에
//           "from '.../CommonModal'"로 가져다 쓰던 모든 화면코드는 수정 없이 그대로 동작함.
/////////////////////////////////////////////////////////////////////////////////////////////////////////
import { createContext, useContext, ReactNode } from "react";

// 지금 그려지는 팝업의 프로그램 정보 - CommonModal이 팝업 내용을 이 Provider로 감싸고, 팝업 안의 NaviMenuBar가 읽어
// DD생성 권한자에게 [DD생성] 버튼을 붙임(2026-09-28). 탭 화면에서는 값이 없음(null).
export type PopupPgmInfo = { pgmId: string; callPgmId: string } | null;
export const PopupPgmContext = createContext<PopupPgmInfo>(null);

// 팝업 안 메뉴바(NaviMenuBar)가 자기 [닫기](btnClose)를 그렸는지 팝업 틀(CommonModal PopupFrame)에 알림 - 그리면 제목줄 [닫기]를 숨김(2026-09-28)
export const PopupCloseContext = createContext<((on: boolean) => void) | null>(null);

// 공통팝업모달
export type ModalCommonPopupOptions = {
  title: string;
  content: ReactNode;
  modalHeight?: string;
  modalWidth?: string;
  gridCellName?: Record<string, any>;
  gridCellCode?: Record<string, any>;
  gridCellWidth?: Record<string, any>;
  sqlFromStatements?: string;
  sqlWhereStatements?: string;
  sqlOrderbyStatements?: string;
  sqlWhereInputCodeValue?: string;
  sqlWhereInputNameValue?: string;
  autoQuery?: boolean;
  onConfirm?: (selectedRow: any) => void;
} | null;

// 사용자팝업모달
export type ModalUserPopupOptions = {
  title: string;
  callComponent: ReactNode | React.ComponentType<any>;
  pgmId?: string;   // 권한 확인용 프로그램ID(없으면 팝업 컴포넌트의 static pgmId 사용) - 권한그룹에 없으면 403 페이지(2026-09-28)
  callPgmId?: string;   // 팝업 화면 경로(예: popup/H2001R1) - 팝업 메뉴바의 DD생성이 소스를 찾을 때 씀(없으면 popup/<pgmId>)
  paramProps?: Record<string, any>;
  modalHeight?: string;
  modalWidth?: string;
  sqlWhereInputCodeValue?: string;
  sqlWhereInputNameValue?: string;
  autoQuery?: boolean;
  onConfirm?: (selectedRow: any) => void;
  skipAuthGate?: boolean;   // 열기 전 권한 확인(403 페이지) 생략 - 팝업메뉴등록(Z2005M1)의 [팝업호출](DD생성용, 조회 안 함)에서만 사용
} | null;

// 레퍼런스팝업모달
export type ModalRefPopupOptions = {
  title: string;
  callComponent: ReactNode | React.ComponentType<any>;
  pgmId?: string;   // 권한 확인용 프로그램ID(없으면 팝업 컴포넌트의 static pgmId 사용) - 권한그룹에 없으면 403 페이지(2026-09-28)
  callPgmId?: string;   // 팝업 화면 경로(예: popup/H2001R1) - 팝업 메뉴바의 DD생성이 소스를 찾을 때 씀(없으면 popup/<pgmId>)
  paramProps?: Record<string, any>;
  modalHeight?: string;
  modalWidth?: string;
  autoQuery?: boolean;
  skipAuthGate?: boolean;   // 열기 전 권한 확인(403 페이지) 생략 - 팝업메뉴등록(Z2005M1)의 [팝업호출](DD생성용, 조회 안 함)에서만 사용
} | null;

// 메세지알림모달
export type ModalMsgBoxOptions = {
  title?: string;
  msgText: string | ReactNode;
  msgDetail?: string | ReactNode;
  showBtn?: string | 'Yes' | 'No' | 'OK' | 'YesNo';
  msgType?: string | 'info' | 'warning' | 'error' | 'success';
  confirmText?: string;
  cancelText?: string;
  btnOK?: () => void;
  btnYes?: () => void;
  btnNo?: () => void;
};

export type ModalContextType = {
  showCommonPopup: (configCommonPopup: Exclude<ModalCommonPopupOptions, null>) => void;
  hideCommonPopup: () => void;
  showMsgBox: (msgBoxOpts: ModalMsgBoxOptions) => void;
  hideMsgBox: () => void;
  confirmMsgBox: (opts: ModalMsgBoxOptions) => Promise<boolean>;
  showUserPopup: (configUserPopup: Exclude<ModalUserPopupOptions, null>) => void;
  hideUserPopup: () => void;
  showRefPopup: (configRefPopup: Exclude<ModalRefPopupOptions, null>) => void;
  hideRefPopup: () => void;
};

export const ModalContext = createContext<ModalContextType | undefined>(undefined);

// 공통팝업모달
export const useCommonPopupModal = () => {
  const ctx = useContext(ModalContext);
  if (!ctx) throw new Error("useCommonPopupModal must be used within a ModalProvider");
  return ctx;
};

// 사용자팝업모달
export const useUserPopupModal = () => {
  const ctx = useContext(ModalContext);
  if (!ctx) throw new Error("useUserPopupModal must be used within a ModalProvider");
  return ctx;
};

// 레퍼런스팝업모달
export const useRefPopupModal = () => {
  const ctx = useContext(ModalContext);
  if (!ctx) throw new Error("useRefPopupModal must be used within a ModalProvider");
  return ctx;
};

// 메시지박스모달
export const useMsgBoxModal = () => {
  const ctx = useContext(ModalContext);
  if (!ctx) throw new Error('useMsgBoxModal must be used within ModalProvider');
  return ctx;
};
